import { useEffect, useRef } from 'react';
import type { WebSocketEventMap } from './websocket.events';
import type { WSMessage } from './websocket.types';
import { wsManager, type Listener } from './websocketManager';

/**
 * Subscribes to a websocket event with a stable internal callback reference.
 * This avoids stale closures while keeping subscription setup minimal.
 */
export function useWebSocketEvent<K extends keyof WebSocketEventMap>(
    event: K,
    callback: Listener<K>
): void {
    const callbackRef = useRef<Listener<K>>(callback);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        const unsubscribe = wsManager.subscribe(event, (data) => {
            callbackRef.current(data);
        });

        return unsubscribe;
    }, [event]);
}

/**
 * Convenience hook for consuming every parsed websocket message.
 */
export function useWebSocketMessages(callback: (message: WSMessage) => void): void {
    useWebSocketEvent('message', callback);
}
